import { planPlaybackForm } from '@/music/ireal';
import type { IRealChart, IRealMeasure } from '@/music/ireal/types';
import { analyzeForm, spanLength } from '@/music/form';
import { buildPerformance, type ArrangementPolicy, type PassKind } from '@/music/performance';
import {
  endingActive,
  type EndingOptions,
  type ResolveEnding,
  type TagBars,
  type TagTimes,
} from '@/audio/endings';
import { chordFromType, measureStartBeats } from '@/music/chart';
import { getChordType, type ChordTypeId } from '@/music/chords';
import { pitchClassOf, spellNote } from '@/music/notes';
import type { IRealChord } from '@/music/ireal/types';
import type { EndingMarks } from '@/components/playbackStatus';

export interface SequencePlaybackForm {
  /**
   * The form is already unrolled into its full performance order, so playback
   * must build its chord sequence from `measures` (not the single-pass chart)
   * and loop it once. True for a detached paired coda and for any arrangement
   * policy that writes the itinerary out.
   */
  preExpanded: boolean;
  measures: IRealMeasure[];
  /** Beat offsets for the logical passes inside an already-expanded form. */
  passStarts?: number[];
  /** Each pass's role under the itinerary (head / solo / head-out) — what
   * lets the band play the convention (bass 2-feel heads, walking solos). */
  passKinds?: PassKind[];
  /**
   * Where a repeated form RE-ENTERS (beats): past the entry material — the
   * pickup plus any intro/verse — which plays once, on the way in. The player
   * generalises its pickup exclusion to this. Only set on forms whose loop
   * mechanics still live in the player (not written-out passes).
   */
  loopStartBeat?: number;
  /** Written intro/verse bars playing once on the way in (UI honesty). */
  entryBars?: number;
  /** Written coda/outro bars playing once on the way out (UI honesty). */
  exitBars?: number;
  /** Where a written-out tag and resolving bar sit, for the playing status. */
  endingMarks?: EndingMarks;
}

/** Beat length of a measure list, honouring meter changes and pickups. */
function beatsOf(measures: IRealMeasure[], timeSignature: [number, number]): number {
  let meter = timeSignature;
  let beats = 0;
  for (const measure of measures) {
    meter = measure.timeSig ?? meter;
    beats += measure.pickupBeats ?? meter[0];
  }
  return beats;
}

/** The meter in force at `index` of an already-flattened measure list. */
function meterAt(
  measures: IRealMeasure[],
  index: number,
  timeSignature: [number, number],
): [number, number] {
  let meter = timeSignature;
  for (let i = 0; i < index && i < measures.length; i += 1) {
    meter = measures[i].timeSig ?? meter;
  }
  return meter;
}

/**
 * A repeated slice must RESTATE the meter it inherits: measure lists carry
 * meter changes positionally, so a loop pass pasted after itself would
 * otherwise pick up whatever meter the previous pass ended in.
 */
function withMeterRestated(slice: IRealMeasure[], meter: [number, number]): IRealMeasure[] {
  if (slice.length === 0 || slice[0].timeSig) return slice;
  return [{ ...slice[0], timeSig: meter }, ...slice.slice(1)];
}

export interface PlaybackFormOptions {
  repeatForm: boolean;
  cycleActive: boolean;
  policy?: ArrangementPolicy;
  /** How the last pass finishes (tag / ritardando / held chord) — see applyEnding. */
  ending?: EndingOptions;
}

/**
 * Write a looping form OUT as `passes` written passes: pass 1 whole (pickup
 * and entry material included), every later pass re-entering at the loop
 * start — the player's own repeat rule, on paper. Used when an ending needs
 * the final bars to exist exactly once in the played form.
 */
export function writeOutPasses(
  fallback: IRealMeasure[],
  passes: number,
  loopStartBeat: number | undefined,
  timeSignature: [number, number],
): { measures: IRealMeasure[]; passStarts: number[] } {
  const starts = measureStartBeats(fallback, timeSignature);
  let loopIndex = 0;
  if (loopStartBeat !== undefined) {
    const found = starts.findIndex((beat) => Math.abs(beat - loopStartBeat) < 1e-6);
    if (found >= 0) loopIndex = found;
  } else if (fallback[0]?.pickupBeats && fallback.length > 1) {
    // The anacrusis plays once, on the way in.
    loopIndex = 1;
  }
  const loop = withMeterRestated(
    fallback.slice(loopIndex),
    meterAt(fallback, loopIndex, timeSignature),
  );
  const measures: IRealMeasure[] = [...fallback];
  const passStarts = [0];
  for (let pass = 1; pass < passes; pass += 1) {
    passStarts.push(beatsOf(measures, timeSignature));
    measures.push(...loop);
  }
  return { measures, passStarts };
}

/**
 * The TAG: the last `bars` measures repeated so they play `times` in all —
 * "tag the last four bars three times". Copies keep their written ids (the
 * sheet's playhead lights the written bar each time round) and restate the
 * meter in force, like every repeated slice.
 */
export function tagEnding(
  measures: IRealMeasure[],
  timeSignature: [number, number],
  bars: TagBars,
  times: TagTimes,
): IRealMeasure[] {
  const n = Math.min(bars, measures.length);
  if (n <= 0 || times <= 1) return measures;
  const from = measures.length - n;
  const tail = withMeterRestated(measures.slice(from), meterAt(measures, from, timeSignature));
  const out = [...measures];
  for (let k = 1; k < times; k += 1) out.push(...tail);
  return out;
}

/** The tune's key as a root spelling and mode; null when the chart names none. */
function keyOf(key: string | undefined): { root: string; pc: number; minor: boolean } | null {
  const trimmed = (key ?? '').trim();
  if (!trimmed) return null;
  const minor = /(minor|-|m)$/i.test(trimmed) && !/^[A-G][b#♭♯]?$/.test(trimmed);
  const root = trimmed
    .replace(/\s*(minor|major|-|m)$/i, '')
    .replace('♭', 'b')
    .replace('♯', '#')
    .trim();
  try {
    return { root, pc: pitchClassOf(root), minor };
  } catch {
    return null;
  }
}

/** The last sounding chord of a measure list (N.C. skipped). */
function lastChord(measures: IRealMeasure[]): IRealChord | null {
  for (let m = measures.length - 1; m >= 0; m -= 1) {
    const chords = measures[m].chords;
    for (let c = chords.length - 1; c >= 0; c -= 1) {
      if (!chords[c].noChord && chords[c].chordType) return chords[c];
    }
  }
  return null;
}

/** Whether a chord already sounds as the key chord (its root, not a dominant or °). */
export function landsHome(chord: IRealChord | null, tonicPc: number): boolean {
  if (!chord?.chordType) return false;
  let pc: number;
  try {
    pc = pitchClassOf(chord.bass ?? chord.root);
  } catch {
    return false;
  }
  const category = getChordType(chord.chordType).category;
  return pc === tonicPc && category !== 'dominant' && category !== 'diminished';
}

/**
 * THE RESOLVING BAR (#429): many charts end on a turnaround that leads back
 * to the top, so the last pass stops on a ii–V. Append a bar on the key chord
 * (a 6/9, or m6 in a minor key), through ♭VI∆ or ♭II∆ first if asked, or the
 * first chord of bar 1 ("stop on the top"). Nothing is added when the last
 * bar already lands home. Playback only: the chart is untouched.
 */
export function resolveEnding(
  measures: IRealMeasure[],
  key: string | undefined,
  timeSignature: [number, number],
  resolve: ResolveEnding,
  topIndex: number,
): IRealMeasure[] {
  if (resolve === 'off' || measures.length === 0) return measures;
  const meter = meterAt(measures, measures.length, timeSignature);
  const bar = (chord: IRealChord, n: number): IRealMeasure => ({
    id: `ending-resolve-${n}`,
    chords: [{ ...chord, id: `ending-resolve-${n}-c`, beats: meter[0] }],
    ...(n === 0 ? { timeSig: meter } : {}),
  });
  if (resolve === 'top') {
    const top = measures
      .slice(topIndex)
      .flatMap((m) => m.chords)
      .find((c) => !c.noChord && c.chordType);
    return top ? [...measures, bar(top, 0)] : measures;
  }
  const home = keyOf(key);
  if (!home || landsHome(lastChord(measures), home.pc)) return measures;
  const flat = (steps: number) => spellNote(home.pc + steps, 'flat');
  const chord = (root: string, type: ChordTypeId) => chordFromType(root, type, meter[0]);
  const tonic = chord(home.root, home.minor ? 'min6' : 'maj69');
  const approach =
    resolve === 'flat-six'
      ? chord(flat(8), 'maj7')
      : resolve === 'flat-two'
        ? chord(flat(1), 'maj7')
        : null;
  const added = approach ? [approach, tonic] : [tonic];
  return [...measures, ...added.map((c, n) => bar(c, n))];
}

/** Bar 1 of the chorus in a built form: past the pickup and any intro. */
function topIndexOf(form: SequencePlaybackForm, timeSignature: [number, number]): number {
  const starts = measureStartBeats(form.measures, timeSignature);
  if (form.loopStartBeat !== undefined) {
    const found = starts.findIndex((beat) => Math.abs(beat - form.loopStartBeat!) < 1e-6);
    if (found >= 0) return found;
  }
  return form.measures[0]?.pickupBeats && form.measures.length > 1 ? 1 : 0;
}

/**
 * Apply the ending to a built playback form. Nothing changes without an
 * ending device on, under ∞ (a loop that never ends has no ending) or under
 * form cycling (a voicing-analysis view that rewrites the form itself). A
 * ritardando or a held chord are windows over the FINAL bars, so those bars
 * must exist exactly once: a form the player would loop N times is written
 * out first. A tag then appends its copies to the final pass.
 */
export function applyEnding(
  form: SequencePlaybackForm,
  chart: Pick<IRealChart, 'repeats' | 'timeSignature' | 'key'>,
  options: PlaybackFormOptions,
): SequencePlaybackForm {
  const ending = options.ending;
  if (!endingActive(ending) || options.repeatForm || options.cycleActive) return form;
  const passes = Math.max(1, chart.repeats ?? 1);
  let measures = form.measures;
  let passStarts = form.passStarts;
  let expanded = form.preExpanded;
  if (!form.preExpanded && passes > 1) {
    ({ measures, passStarts } = writeOutPasses(
      form.measures,
      passes,
      form.loopStartBeat,
      chart.timeSignature,
    ));
    expanded = true;
  }
  const endingMarks: EndingMarks = {};
  if (ending!.tagBars > 0) {
    const bars = Math.min(ending!.tagBars, measures.length);
    const tagged = tagEnding(measures, chart.timeSignature, ending!.tagBars, ending!.tagTimes);
    if (tagged !== measures) {
      endingMarks.tag = { start: measures.length - bars, bars, times: ending!.tagTimes };
    }
    measures = tagged;
    expanded = true;
  }
  if (ending!.resolve !== 'off') {
    const resolved = resolveEnding(
      measures,
      chart.key,
      chart.timeSignature,
      ending!.resolve,
      topIndexOf(form, chart.timeSignature),
    );
    if (resolved !== measures) {
      endingMarks.resolveStart = measures.length;
      measures = resolved;
      expanded = true;
    }
  }
  if (!expanded) return form;
  const { loopStartBeat: _loop, ...rest } = form;
  void _loop;
  return {
    ...rest,
    preExpanded: true,
    measures,
    passStarts: passStarts ?? [0],
    ...(endingMarks.tag || endingMarks.resolveStart !== undefined ? { endingMarks } : {}),
  };
}

/** Build the playback-only form while leaving the engraved chart unchanged. */
export function buildSequencePlaybackForm(
  chart: Pick<IRealChart, 'measures' | 'repeats' | 'timeSignature' | 'key'>,
  fallback: IRealMeasure[],
  options: PlaybackFormOptions,
): SequencePlaybackForm {
  return applyEnding(basePlaybackForm(chart, fallback, options), chart, options);
}

function basePlaybackForm(
  chart: Pick<IRealChart, 'measures' | 'repeats' | 'timeSignature'>,
  fallback: IRealMeasure[],
  options: PlaybackFormOptions,
): SequencePlaybackForm {
  // The jazz itinerary wins when chosen: head, N solo choruses, head out
  // taking the coda. Form cycling is a voicing-analysis view that rewrites the
  // form itself, so the two never combine.
  if (options.policy === 'head-solos-head' && !options.cycleActive) {
    const plan = buildPerformance(chart as IRealChart, {
      policy: 'head-solos-head',
      // Under the itinerary ×N counts the SOLO choruses; the head (as
      // written, twice under a bare repeat) and the head out are implied.
      solos: Math.max(1, chart.repeats ?? 1),
    });
    if (plan.policy === 'head-solos-head') {
      if (options.repeatForm) {
        // An open-ended performance states the head once, then stays in the
        // first solo chorus. The head out/coda is an ending and cannot be
        // part of a loop that never finishes.
        const soloPass = plan.passKinds.indexOf('solo');
        if (soloPass >= 0) {
          const soloStart = plan.passStartIndices[soloPass];
          const soloEnd = plan.passStartIndices[soloPass + 1] ?? plan.measures.length;
          const measures = plan.measures.slice(0, soloEnd);
          const passStarts = plan.passStartIndices
            .slice(0, soloPass + 1)
            .map((index) => beatsOf(measures.slice(0, index), chart.timeSignature));
          return {
            preExpanded: true,
            measures,
            passStarts,
            passKinds: plan.passKinds.slice(0, soloPass + 1),
            loopStartBeat: beatsOf(measures.slice(0, soloStart), chart.timeSignature),
          };
        }
      }
      // Pass starts as beats, so the transport can report "head / solo 2 / out".
      const passStarts = plan.passStartIndices.map((index) =>
        beatsOf(plan.measures.slice(0, index), chart.timeSignature),
      );
      return { preExpanded: true, measures: plan.measures, passStarts, passKinds: plan.passKinds };
    }
  }

  // ENTRY / LOOP / EXIT (as-written repeats, the human reading): the intro or
  // verse plays once on the way in, ×N and ∞ loop the CHORUS, and a trailing
  // named coda plays once on the way out. Read from `analyzeForm` for charts
  // with no navigation marks — a chart with a segno/⊕/D.S. keeps its literal
  // reading and the paired-coda plan below, which own those semantics.
  const form = analyzeForm(chart as IRealChart);
  const hasNavMarks = chart.measures.some((m) => m.segno || m.coda || m.directive != null);
  const entryBars = !hasNavMarks && form.intro ? spanLength(form.intro) : 0;
  const exitBars =
    !hasNavMarks && form.coda && form.coda.start > form.chorus.end ? spanLength(form.coda) : 0;
  // The shape is a fact about the chart, reported even at ×1 — the Repeats
  // panel explains what ×N WILL loop before anyone touches the slider.
  const meta = { entryBars, exitBars };
  const passes = Math.max(1, chart.repeats ?? 1);
  const repeating = options.repeatForm || passes > 1;

  if (!options.cycleActive && (entryBars > 0 || exitBars > 0)) {
    // Everything is located in the LITERAL flatten — the played form is
    // already right; the work is telling the player where the loop re-enters
    // and, when there is exit material, writing the passes out so the tail
    // sounds only once.
    const loopTopId = chart.measures[form.chorus.start]?.id;
    const loopStartIndex = fallback.findIndex((m) => m.id === loopTopId);
    const exitIds = new Set(
      exitBars > 0 ? chart.measures.slice(form.chorus.end + 1).map((m) => m.id) : [],
    );
    let cut = fallback.length;
    while (cut > 0 && exitIds.has(fallback[cut - 1].id)) cut -= 1;
    const readable =
      loopStartIndex >= 0 &&
      (entryBars === 0 || loopStartIndex > 0) &&
      (exitBars === 0 || cut < fallback.length) &&
      cut > loopStartIndex;
    if (readable) {
      const loopStartBeat = beatsOf(fallback.slice(0, loopStartIndex), chart.timeSignature);
      if (exitBars === 0) {
        // Intro only: the literal form plays as-is; later passes re-enter at
        // the chorus top. All the player's loop machinery rides along.
        // Carried even at ×1 — the player snapshots it at Play, and a live
        // repeat bump mid-flight must find the loop point already in place.
        return { preExpanded: false, measures: fallback, loopStartBeat, ...meta };
      }
      if (!repeating) {
        // A single as-written pass with a tail IS the literal reading.
        return { preExpanded: false, measures: fallback, loopStartBeat, ...meta };
      }
      if (options.repeatForm) {
        // ∞ never reaches an ending: drop the exit, re-enter past the entry.
        return {
          preExpanded: true,
          measures: fallback.slice(0, cut),
          loopStartBeat,
          ...meta,
        };
      }
      // Finite ×N with a tail: entry + loop ×N + exit, written out — the same
      // shape as the paired-coda plan below, with the entry split off pass 1.
      const loop = withMeterRestated(
        fallback.slice(loopStartIndex, cut),
        meterAt(fallback, loopStartIndex, chart.timeSignature),
      );
      const exit = withMeterRestated(
        fallback.slice(cut),
        meterAt(fallback, cut, chart.timeSignature),
      );
      const measures: IRealMeasure[] = [...fallback.slice(0, cut)];
      for (let pass = 1; pass < passes; pass += 1) measures.push(...loop);
      measures.push(...exit);
      const loopBeats = beatsOf(loop, chart.timeSignature);
      return {
        preExpanded: true,
        measures,
        passStarts: Array.from({ length: passes }, (_, pass) =>
          pass === 0 ? 0 : loopStartBeat + pass * loopBeats,
        ),
        ...meta,
      };
    }
  }

  const plan = planPlaybackForm(chart.measures);
  const detached =
    (plan.hasDetachedCoda || plan.endsAtMarker) &&
    !options.cycleActive &&
    (options.repeatForm || Math.max(1, chart.repeats ?? 1) > 1);

  // At ×1 this is already the final pass: take the detached coda instead of
  // playing the ordinary tail followed by the coda. The same rule honours
  // iReal's `U` marker; material after it belongs only to passes that loop.
  if (!detached && (plan.hasDetachedCoda || plan.endsAtMarker) && !options.cycleActive) {
    return { preExpanded: true, measures: plan.finalPass, passStarts: [0], ...meta };
  }
  if (!detached) return { preExpanded: false, measures: fallback, ...meta };
  if (options.repeatForm) return { preExpanded: true, measures: plan.loopPass, ...meta };

  // Both passes are flattened from the top, so every pass after the first
  // re-enters at bar 1: the anacrusis plays once, on the way in (the player's
  // own repeat rule, and writeOutPasses'), and each pasted pass restates the
  // meter it starts in.
  const reentry = (pass: IRealMeasure[]): IRealMeasure[] => {
    const lead = pass[0]?.pickupBeats && pass.length > 1 ? 1 : 0;
    return withMeterRestated(pass.slice(lead), meterAt(pass, lead, chart.timeSignature));
  };
  const loopAgain = reentry(plan.loopPass);
  const measures: IRealMeasure[] = [...plan.loopPass];
  const passStarts = [0];
  for (let pass = 1; pass < passes; pass += 1) {
    passStarts.push(beatsOf(measures, chart.timeSignature));
    measures.push(...(pass < passes - 1 ? loopAgain : reentry(plan.finalPass)));
  }

  return { preExpanded: true, measures, passStarts, ...meta };
}
